/**
 * CSP nonce bootstrap (ZAP "CSP: style-src unsafe-inline").
 *
 * nginx sends `style-src 'self' 'nonce-<random per request>'` (no 'unsafe-inline') and writes the same nonce into
 * <meta property="csp-nonce" nonce="__CSP_NONCE__"> in index.html. Libraries that inject <style> tags at runtime
 * (antd cssinjs, styled-components, emotion/MUI, PrimeReact, rc-util scroll locker) need that nonce on every tag
 * or the browser blocks them, so this module must be imported before anything else renders.
 *
 * - Every <style> element created by script gets the nonce. HTML-injected <style> tags still get blocked, which
 *   is what the CSP is for.
 * - el.setAttribute('style', ...) is rewritten to el.style.cssText = ... (CSSOM, which CSP allows) because
 *   some libraries (rc-textarea autoSize, antd Watermark) set inline styles that way.
 */
const NONCE_PLACEHOLDER = '__CSP_NONCE__';

const readNonce = (): string | undefined => {
  const meta = document.head.querySelector<HTMLMetaElement>('meta[property="csp-nonce"]');
  // Browsers hide the nonce attribute once the page has a CSP, so read the property first
  const nonce = meta?.nonce || meta?.getAttribute('nonce') || '';
  // Dev server / no nginx: placeholder was never replaced and there is no CSP to satisfy
  return nonce && nonce !== NONCE_PLACEHOLDER ? nonce : undefined;
};

export const cspNonce = readNonce();

if (cspNonce) {
  const createElement = document.createElement;
  document.createElement = function (this: Document, tagName: string, options?: ElementCreationOptions) {
    const el = createElement.call(this, tagName, options);
    if (el instanceof HTMLStyleElement) el.nonce = cspNonce;
    return el;
  } as typeof document.createElement;

  const setAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (this: Element, name: string, value: string) {
    if (name.toLowerCase() === 'style' && (this instanceof HTMLElement || this instanceof SVGElement)) {
      this.style.cssText = value;
      return;
    }
    setAttribute.call(this, name, value);
  };
}
